import React from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { ArrowLeft, Download } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Alert as UiAlert } from "../../components/ui/Feedback";
import { findCompanyLegalDocument } from "../../template/TemplateCompanyUtils.js";

const spacingValue = (value) => (typeof value === "number" ? `${value * 8}px` : value);

const sxToStyle = (sx = {}) => {
  const style = {};

  Object.entries(sx || {}).forEach(([key, value]) => {
    if (key === "mt") style.marginTop = spacingValue(value);
    else if (key === "mb") style.marginBottom = spacingValue(value);
    else if (key === "mr") style.marginRight = spacingValue(value);
    else if (key === "pb") style.paddingBottom = spacingValue(value);
    else if (key === "pl") style.paddingLeft = spacingValue(value);
    else if (key === "ml") style.marginLeft = spacingValue(value);
    else if (key === "my") {
      style.marginTop = spacingValue(value);
      style.marginBottom = spacingValue(value);
    } else if (key === "px") {
      style.paddingLeft = spacingValue(value);
      style.paddingRight = spacingValue(value);
    } else if (key === "py") {
      style.paddingTop = spacingValue(value);
      style.paddingBottom = spacingValue(value);
    } else if (key === "bgcolor") {
      style.backgroundColor = value === "grey.50" ? "#f8fafc" : value;
    } else if (key === "color") {
      style.color = value === "primary.main" ? "#195790" : value === "text.secondary" ? "#64748b" : value;
    } else if (key === "borderColor") {
      style.borderColor = value === "primary.main" ? "#195790" : value;
    } else if (!String(key).startsWith("&")) {
      style[key] = value;
    }
  });

  return style;
};

const Box = ({ children, className, dangerouslySetInnerHTML, sx, style }) => {
  const mergedStyle = { ...sxToStyle(sx), ...(style || {}) };

  if (dangerouslySetInnerHTML) {
    return <div className={className} dangerouslySetInnerHTML={dangerouslySetInnerHTML} style={mergedStyle} />;
  }

  return <div className={className} style={mergedStyle}>{children}</div>;
};

const Container = ({ children, sx }) => (
  <main className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8" style={sxToStyle(sx)}>
    {children}
  </main>
);

const typographyTags = {
  h4: "h1",
  h6: "h2",
  body2: "p",
  caption: "span"
};

const Typography = ({ children, className, color, component, sx, variant }) => {
  const Component = component || typographyTags[variant] || "p";
  const style = {
    ...(color === "text.secondary" ? { color: "#64748b" } : {}),
    ...sxToStyle(sx)
  };

  return <Component className={className} style={style}>{children}</Component>;
};

const Alert = ({ children, severity = "info" }) => (
  <UiAlert tone={severity}>{children}</UiAlert>
);

const Button = ({ children, onClick, size = "medium", startIcon, variant = "text" }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center justify-center gap-2 rounded border font-semibold transition ${size === "small" ? "px-3 py-1.5 text-sm" : size === "large" ? "px-5 py-3" : "px-4 py-2 text-sm"
      } ${variant === "contained"
        ? "border-[#195790] bg-[#195790] text-white hover:bg-[#12466f]"
        : "border-[#195790] bg-white text-[#195790] hover:bg-blue-50"
      }`}
  >
    {startIcon}
    {children}
  </button>
);

const IconButton = ({ children, onClick }) => (
  <button
    type="button"
    aria-label="back"
    onClick={onClick}
    className="mr-4 inline-flex h-10 w-10 items-center justify-center rounded-full text-slate-700 transition hover:bg-slate-100"
  >
    {children}
  </button>
);

const PolicyPage = () => {
  const { name } = useParams();
  const navigate = useNavigate();
  const company = useSelector((state) => state.companyReducer.company);

  const legalDocument = findCompanyLegalDocument(company, name);

  const displayLink = legalDocument?.displayLink || legalDocument?.storedLink;
  const hasFileDocument = Boolean(displayLink);

  // Format policy name for display
  const formatPolicyName = (contentSubType) => {
    if (!contentSubType) return "Policy";
    return contentSubType
      .toLowerCase()
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Get file extension
  const getFileExtension = (url) => {
    if (!url) return null;
    const urlWithoutParams = String(url).split(/[?#]/)[0];
    const extensionMatch = urlWithoutParams.match(/\.([a-z0-9]+)$/i);
    return extensionMatch?.[1]?.toLowerCase() || null;
  };

  const fileExtension = getFileExtension(displayLink)
    || getFileExtension(legalDocument?.name)
    || String(legalDocument?.fileType || '').replace(/^\./, '').toLowerCase()
    || (String(legalDocument?.contentType || '').toLowerCase() === 'application/pdf' ? 'pdf' : null);

  const isPdfFile = fileExtension === "pdf";
  const isDocFile = ["doc", "docx", "txt"].includes(fileExtension);
  const isImageFile = ["jpg", "jpeg", "png", "gif", "bmp"].includes(fileExtension);

  // Download file handler
  const handleDownload = () => {
    if (displayLink) {
      const link = document.createElement("a");
      link.href = displayLink;
      link.download = legalDocument?.name || `document_${name}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const renderOfficeDocument = () => {
    const viewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(displayLink)}`;

    return (
      <Box sx={{ mt: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <Button
            variant="outlined"
            onClick={handleDownload}
            startIcon={<Download />}
          >
            Download {fileExtension.toUpperCase()}
          </Button>
        </Box>

        <iframe
          src={viewerUrl}
          title={legalDocument?.name}
          width="100%"
          height="800px"
          style={{
            border: "none"
          }}
        />
      </Box>
    );
  };

  // Navigate back
  const handleBack = () => {
    navigate(-1);
  };

  // Render PDF content without borders
  const renderPdfContent = () => {
    return (
      <Box sx={{ mt: 3 }}>

        {/* PDF embedded without any borders or paper styling */}
        <iframe
          className="policy-page-pdf"
          src={`${displayLink}#toolbar=0&navpanes=0&scrollbar=0&statusbar=0&view=FitH`}
          title={legalDocument?.name || "Document"}
          width="100%"
          height="800px"
          style={{
            border: 'none',
            display: 'block',
            borderRadius: '0'
          }}
        />
      </Box>
    );
  };

  // Render image files without borders
  const renderImageDocument = () => {
    return (
      <Box sx={{ mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="outlined"
            onClick={handleDownload}
            startIcon={<Download />}
          >
            Download Image
          </Button>
        </Box>
        {/* Image without paper border */}
        <Box sx={{ textAlign: 'center' }}>
          <img
            src={displayLink}
            alt={legalDocument?.name || "Document"}
            style={{
              maxWidth: '100%',
              maxHeight: '70vh',
              objectFit: 'contain'
            }}
          />
        </Box>
      </Box>
    );
  };

  // Render other file types with download option
  const renderDownloadOnlyDocument = () => {
    return (
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          {fileExtension ?
            `This ${fileExtension.toUpperCase()} document cannot be displayed in the browser.` :
            "This document cannot be displayed in the browser."
          }
        </Alert>
        <Button
          variant="contained"
          onClick={handleDownload}
          size="large"
          startIcon={<Download />}
        >
          Download {fileExtension ? fileExtension.toUpperCase() : 'Document'}
        </Button>
        {legalDocument?.name && (
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
            File: {legalDocument.name}
          </Typography>
        )}
      </Box>
    );
  };

  const renderFileDocument = () => {
    if (isPdfFile) {
      return renderPdfContent();
    }

    if (isImageFile) {
      return renderImageDocument();
    }

    if (isDocFile) {
      return renderOfficeDocument();
    }

    return renderDownloadOnlyDocument();
  };

  // Render HTML content from description
  const renderHtmlContent = () => {
    return (
      <div
        className="policy-document-content"
        dangerouslySetInnerHTML={{ __html: legalDocument.description }}
      />
    );
  };

  // Main content renderer
  const renderContent = () => {
    if (!legalDocument) {
      return (
        <Alert severity="info">
          Policy document not found. Please check the URL or contact support.
        </Alert>
      );
    }

    // Priority 1: If HTML description exists
    if (legalDocument.description && legalDocument.description.trim() !== '') {
      return renderHtmlContent();
    }

    // Priority 2: If file document exists
    if (hasFileDocument) {
      return renderFileDocument();
    }

    // Fallback - no content available
    return (
      <Alert severity="warning">
        No content available for this policy. Please contact support for more information.
      </Alert>
    );
  };

  return (
    <Box className="policy-page" sx={{ flexGrow: 1 }} style={{ paddingTop: "96px" }}>
      <style>{`
        .policy-document-content {
          line-height: 1.8;
        }
        .policy-document-content p {
          margin-bottom: 16px;
          font-size: 1rem;
        }
        .policy-document-content h1 {
          margin-top: 32px;
          margin-bottom: 24px;
          border-bottom: 2px solid #195790;
          padding-bottom: 8px;
          color: #195790;
          font-size: 2rem;
          font-weight: 700;
        }
        .policy-document-content h2 {
          margin-top: 24px;
          margin-bottom: 16px;
          color: #195790;
          font-size: 1.75rem;
          font-weight: 700;
        }
        .policy-document-content h3 {
          margin-top: 24px;
          margin-bottom: 16px;
          color: #195790;
          font-size: 1.5rem;
          font-weight: 700;
        }
        .policy-document-content h4,
        .policy-document-content h5,
        .policy-document-content h6 {
          margin-top: 16px;
          margin-bottom: 16px;
          color: #195790;
          font-size: 1.25rem;
          font-weight: 700;
        }
        .policy-document-content strong {
          font-weight: 700;
        }
        .policy-document-content em {
          font-style: italic;
        }
        .policy-document-content ul,
        .policy-document-content ol {
          margin-bottom: 16px;
          padding-left: 24px;
        }
        .policy-document-content ul li {
          margin-bottom: 8px;
          list-style-type: disc;
        }
        .policy-document-content ol li {
          margin-bottom: 8px;
          list-style-type: decimal;
        }
        .policy-document-content blockquote {
          margin: 24px 0 24px 16px;
          border-left: 4px solid #195790;
          border-radius: 0 8px 8px 0;
          background: #f8fafc;
          padding: 16px 24px;
          font-style: italic;
        }
      `}</style>
      {/* Main Content */}
      <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
        {/* Back navigation */}
        <IconButton
          edge="start"
          color="inherit"
          onClick={handleBack}
          aria-label="back"
        >
          <ArrowLeft />
        </IconButton>

        {/* Content Header */}
        <Box sx={{ mb: 4 }}>
          <Typography
            className="policy-page-title"
            variant="h4"
            sx={{
              fontWeight: 'bold',
              color: 'primary.main',
              textAlign: 'center',
              fontSize: 'clamp(1.75rem, 3vw, 2.5rem)',
              lineHeight: 1.15
            }}
          >
            {formatPolicyName(legalDocument?.contentSubType || name)}
          </Typography>
        </Box>

        {/* Document Content */}
        {renderContent()}

        {/* Last updated info */}
        {legalDocument?.createdDate && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'block',
              textAlign: 'center',
              mt: 4
            }}
          >
            Last updated: {new Date(legalDocument.createdDate).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </Typography>
        )}
      </Container>
    </Box>
  );
};

export default PolicyPage;
